var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useState, useEffect, useRef } from "react";
import { useDebounce } from "@ui/utils/useDebounce";
import { useSelector, useDispatch } from "react-redux";
import { getAllUsers, toggleUserStatus, } from "../../../../reducers/users/thunk/user-thunk";
import { updateFilters, setGlobalSearch, } from "../../../../reducers/users/slice/user-slice";
import { toast } from "@ui/utils/toaster";
import { useTranslation } from "react-i18next";
import { userService } from "@utility/user-service";
export const useUsersFilter = (sortState, currentTab) => {
    const dispatch = useDispatch();
    const { t } = useTranslation(["common"]);
    const { loading, paginationInfo, userFilters, currentPageNumber, currentPageSize, } = useSelector((state) => state.user);
    const [localSearchTerm, setLocalSearchTerm] = useState(userFilters.globalSearch || "");
    const { users } = useSelector((state) => state.user);
    // Track previous values to prevent duplicate calls
    const prevQueryRef = useRef("");
    // Debounce the search term
    const debouncedSearchTerm = useDebounce(localSearchTerm, 500);
    // Sync local search term when global search is cleared externally
    useEffect(() => {
        if (!userFilters.globalSearch && localSearchTerm) {
            setLocalSearchTerm("");
        }
    }, [userFilters.globalSearch]);
    // Update global search in Redux when debounced search term changes
    useEffect(() => {
        if (debouncedSearchTerm !== userFilters.globalSearch) {
            dispatch(setGlobalSearch(debouncedSearchTerm || undefined));
        }
    }, [debouncedSearchTerm, dispatch]);
    useEffect(() => {
        // Map frontend field to backend field (only if sortState exists)
        let sortColumn = sortState === null || sortState === void 0 ? void 0 : sortState.field;
        let sortDirection = sortState === null || sortState === void 0 ? void 0 : sortState.order;
        if (sortColumn === "name") {
            sortColumn = "firstName";
        }
        else if (sortColumn === "lastLoginAt") {
            sortColumn = "lastLoginAt";
        }
        else if (sortColumn === "userId") {
            sortColumn = "userId";
        }
        const query = {
            filters: userFilters,
            pageNumber: currentPageNumber,
            pageSize: currentPageSize,
            sortColumn: sortColumn || undefined,
            sortDirection: sortDirection || undefined,
        };
        // Prevent duplicate calls by comparing query
        const queryString = JSON.stringify(query);
        if (queryString !== prevQueryRef.current) {
            prevQueryRef.current = queryString;
            dispatch(getAllUsers(query));
        }
    }, [currentPageNumber, currentPageSize, userFilters, sortState]);
    const cleanFilterValues = (filters) => {
        const cleaned = {};
        Object.keys(filters).forEach((key) => {
            const value = filters[key];
            if (Array.isArray(value)) {
                cleaned[key] = value.length > 0 ? value : [];
            }
            else if (value === "" || value === null) {
                cleaned[key] = undefined;
            }
            else {
                cleaned[key] = value;
            }
        });
        return cleaned;
    };
    const handleFilterChange = (newFilters) => {
        const cleanedFilters = cleanFilterValues(newFilters);
        // Convert isActive from "Active"/"Inactive" string array to boolean
        if (cleanedFilters.isActive) {
            if (Array.isArray(cleanedFilters.isActive)) {
                const firstValue = cleanedFilters.isActive[0];
                if (firstValue === "Active") {
                    cleanedFilters.isActive = true;
                }
                else if (firstValue === "Inactive") {
                    cleanedFilters.isActive = false;
                }
                else {
                    cleanedFilters.isActive = undefined;
                }
            }
        }
        dispatch(updateFilters(cleanedFilters));
    };
    const handleSearchChange = (event) => {
        setLocalSearchTerm(event.target.value);
    };
    const handleClearFilters = () => {
        let isActiveValue = undefined;
        if (currentTab === "active") {
            isActiveValue = true;
        }
        else if (currentTab === "inactive") {
            isActiveValue = false;
        }
        else {
            isActiveValue = null;
        }
        setLocalSearchTerm("");
        // Clear all filter fields including both 'name' (from GridFilters) and 'userName' (Redux field)
        dispatch(updateFilters({
            name: undefined,
            userName: undefined,
            userId: undefined,
            roleIds: [],
            lastLoginAt: undefined,
            accessPointUids: [],
            isActive: isActiveValue,
            globalSearch: undefined,
        }));
    };
    const handleToggleActive = (user) => __awaiter(void 0, void 0, void 0, function* () {
        // Prevent users from disabling their own account
        const currentUser = userService.getUser();
        if (currentUser && currentUser.id === user.id && user.isActive) {
            toast.error(t("common:toasts.cannotDisableOwnAccount"));
            return;
        }
        yield dispatch(toggleUserStatus({ id: user.id, isActive: !user.isActive }))
            .unwrap()
            .then(() => {
            toast.success(t("common:toasts.userStatusUpdatedSuccessfully"));
            // Refresh the user list with current filters to reflect the status change
            let sortColumn = sortState === null || sortState === void 0 ? void 0 : sortState.field;
            let sortDirection = sortState === null || sortState === void 0 ? void 0 : sortState.order;
            if (sortColumn === "name") {
                sortColumn = "firstName";
            }
            else if (sortColumn === "lastLoginAt") {
                sortColumn = "lastLoginAt";
            }
            else if (sortColumn === "userId") {
                sortColumn = "userId";
            }
            const query = {
                filters: userFilters,
                pageNumber: currentPageNumber,
                pageSize: currentPageSize,
                sortColumn: sortColumn || undefined,
                sortDirection: sortDirection || undefined,
            };
            dispatch(getAllUsers(query));
        })
            .catch((error) => {
            toast.error(typeof error === "string"
                ? error
                : t("common:toasts.failedToUpdateUserStatus"));
        });
    });
    return {
        users: users || [],
        loading,
        searchTerm: localSearchTerm,
        filters: userFilters,
        paginationInfo,
        handleFilterChange,
        handleSearchChange,
        handleClearFilters,
        handleToggleActive,
    };
};
